import { createContext, forwardRef, useContext, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { AiAsterisk } from '../icons';

export type ButtonVariant = 'default' | 'dark' | 'quiet' | 'ai' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  pill?: boolean;
}

/** LG 4.1: default, dark (one per page), quiet, ai (only to confirm what the AI proposes). */
/** Writes to the server in progress (the app passes react-query's useIsMutating): the clicked button waits for them. */
const BusyContext = createContext(0);
export function BusyProvider({ count, children }: { count: number; children: ReactNode }) {
  return <BusyContext.Provider value={count}>{children}</BusyContext.Provider>;
}

/**
 * Loading on the clicked button (04/11/2026): a click that starts a write (or returns a promise) shows a spinner and
 * disables the button until it ends; a click that writes nothing (opens a dialog, navigates) changes nothing.
 */
function useBusy(busyCount: number) {
  const [state, setState] = useState<null | 'wait' | 'busy'>(null);
  const base = useRef(0);
  useEffect(() => {
    if (state === 'wait') {
      if (busyCount > base.current) return setState('busy');
      const t = setTimeout(() => setState((s) => (s === 'wait' ? null : s)), 150);
      return () => clearTimeout(t);
    }
    if (state === 'busy' && busyCount === 0) setState(null);
  }, [state, busyCount]);
  return {
    busy: state === 'busy',
    arm: () => {
      base.current = busyCount;
      setState('wait');
    },
    track: (p: Promise<unknown>) => {
      setState('busy');
      void p.finally(() => setState(null)).catch(() => undefined);
    },
  };
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', size = 'md', pill, className, type = 'button', onClick, disabled, children, ...rest },
  ref,
) {
  const { busy, arm, track } = useBusy(useContext(BusyContext));
  const cls = ['btn', variant !== 'default' ? variant : '', size === 'sm' ? 'sm' : '', pill ? 'pill' : '', busy ? 'busy' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button
      ref={ref}
      type={type}
      className={cls}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      onClick={(e) => {
        const r: unknown = onClick?.(e);
        if (r instanceof Promise) track(r);
        else if (onClick || type === 'submit') arm();
      }}
      {...rest}
    >
      {busy && <span className="btn-spinner" aria-hidden />}
      {children}
    </button>
  );
});

export const btnClass = (variant: ButtonVariant = 'default', extra = '') =>
  ['btn', variant !== 'default' ? variant : '', extra].filter(Boolean).join(' ');

export function Card({ children, className, as: Tag = 'section', ...rest }: { children: ReactNode; className?: string; as?: 'section' | 'div' | 'aside' } & Record<string, unknown>) {
  return (
    <Tag className={['card', className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </Tag>
  );
}

export function PageHeader({ eyebrow, title, lead, actions, back }: { eyebrow?: ReactNode; title: ReactNode; lead?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <div className="stack" style={{ gap: 10 }}>
      {back}
      <div className="page-head">
        <div>
          {eyebrow && <div className="eyebrow">{eyebrow}</div>}
          <h1 style={{ marginTop: eyebrow ? 4 : 0 }}>{title}</h1>
          {lead && <p className="lead">{lead}</p>}
        </div>
        {actions && <div className="actions">{actions}</div>}
      </div>
    </div>
  );
}

/** The AI note inside a row: indigo background, symbol, client name (LG 5.3). */
export function AiNote({ client, children }: { client: string; children: ReactNode }) {
  return (
    <div className="ai-note">
      <AiAsterisk />
      <span>
        <strong>{client}:</strong> {children}
      </span>
    </div>
  );
}

export function AiMark({ children }: { children: ReactNode }) {
  return (
    <span className="ai-mark">
      <AiAsterisk size={12} strokeWidth={2.6} />
      {children}
    </span>
  );
}

export type HealthState = 'good' | 'attention' | 'risk';

/** LG 4.4: shape + colour + word, readable in black and white. */
export function HealthLight({ state, label }: { state: HealthState; label: string }) {
  const shape =
    state === 'good' ? (
      <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="5.5" fill="currentColor" /></svg>
    ) : state === 'attention' ? (
      <svg aria-hidden="true" width="14" height="13" viewBox="0 0 14 13"><path d="M7 0.5 13.5 12.5H0.5Z" fill="currentColor" /></svg>
    ) : (
      <svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13"><path d="M6.5 0.5 12.5 6.5 6.5 12.5 0.5 6.5Z" fill="currentColor" /></svg>
    );
  return (
    <span className={`health ${state}`}>
      {shape}
      {label}
    </span>
  );
}

export function EmptyState({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty stack" style={{ gap: 12, alignItems: 'flex-start' }}>
      <p>{children}</p>
      {action}
    </div>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="stack" style={{ gap: 10, padding: 20 }} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${90 - i * 15}%`, height: i === 0 ? 20 : 14 }} />
      ))}
    </div>
  );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'bad'; children: ReactNode }) {
  return (
    <div className={`notice ${tone === 'info' ? '' : tone}`} role={tone === 'bad' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Chip({ tone, children }: { tone?: 'ok' | 'warn' | 'bad' | 'urgent'; children: ReactNode }) {
  return <span className={['chip', tone ?? ''].filter(Boolean).join(' ')}>{children}</span>;
}

export function Figure({ label, value, of, sentence, sentenceTone }: { label: ReactNode; value: ReactNode; of?: ReactNode; sentence?: ReactNode; sentenceTone?: 'urgent' }) {
  return (
    <div>
      <div className="figure-row">
        <span className="text-2">{label}</span>
        <strong className="figure">
          {value} {of && <span className="of">{of}</span>}
        </strong>
      </div>
      {sentence && (
        <div className="small" style={{ marginTop: 6, color: sentenceTone === 'urgent' ? 'var(--urgent)' : 'var(--text-muted)' }}>
          {sentence}
        </div>
      )}
    </div>
  );
}

export function ProgressBar({ value, tone, label }: { value: number; tone?: 'ok' | 'urgent'; label: string }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="bar" role="progressbar" aria-label={label} aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <i className={tone ?? ''} style={{ width: `${pct}%` }} />
    </div>
  );
}
